
import React, { useContext, useState } from 'react';
import { AppContext } from '../context/AppContext';
import { Page } from '../types';
import * as motion from 'motion/react-m';
import { AnimatePresence } from 'motion/react';
import { Logo } from './Logo';

export const NavbarOriginal: React.FC = () => {
    const context = useContext(AppContext);
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

    if (!context) return null;
    const { page, setPage, cart, footerInfo, setIsCartOpen, isCartOpen, isCheckoutOpen } = context;

    const cartCount = cart.reduce((acc, item) => acc + item.quantity, 0);

    const navItems = [
        { label: 'Accueil', page: Page.Home },
        { label: 'Réparations', page: Page.Repairs },
        { label: 'Acheter', page: Page.BuyPhone },
        { label: 'Revendre', page: Page.SellPhone },
        { label: 'Accessoires', page: Page.Accessories },
        { label: 'Blog Tech', page: Page.Blog }, // Added Blog
        { label: 'Appareils', page: Page.Devices },
        { label: 'Vérifier IMEI', page: Page.ImeiVerification },
        { label: 'Contact', page: Page.Contact },
    ];
// ... rest of Navbar (keep as is but with updated navItems)
    const handleNavClick = (targetPage: Page) => {
        setPage(targetPage);
        setIsMobileMenuOpen(false);
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const iconBtnClass = "w-9 h-9 flex items-center justify-center rounded-xl transition-all duration-300 active:scale-90 shadow-sm border border-gray-50 bg-white/50 hover:bg-white hover:border-blue-100 hover:shadow-md";

    return (
        <nav className="sticky top-0 z-[999] bg-white/80 backdrop-blur-xl border-b border-gray-100 px-4 sm:px-6 py-2.5 md:py-4">
            <div className="max-w-7xl mx-auto flex justify-between items-center">
                <div className="flex items-center gap-2.5 cursor-pointer shrink-0 group" onClick={() => handleNavClick(Page.Home)}>
                    {footerInfo.logo_url ? (
                        <div className="h-10 sm:h-12 flex items-center">
                            <img src={footerInfo.logo_url} alt={footerInfo.siteName || footerInfo.nom_du_magasin} className="h-full w-auto object-contain transition-transform group-hover:scale-105" width="160" height="40" />
                        </div>
                    ) : (
                        <>
                            <Logo variant="modern" size={36} className="group-hover:rotate-6 transition-transform duration-300 shadow-lg shadow-primary/20" />
                            <div className="flex flex-col leading-none">
                                <span className="text-base sm:text-xl font-black text-black tracking-tighter uppercase">
                                    {footerInfo.siteName || footerInfo.nom_du_magasin || 'MOBILBO'}
                                </span>
                                <span className="text-[8px] sm:text-[9px] font-black text-primary uppercase tracking-[0.2em] mt-0.5 opacity-90">
                                    Réparation &amp; vente
                                </span>
                            </div>
                        </>
                    )}
                </div>
                <div className="hidden lg:flex items-center gap-5 xl:gap-7">
                    {navItems.map((item) => (
                        <button key={item.page} onClick={() => handleNavClick(item.page)} className={`text-[9px] xl:text-[10px] font-black uppercase tracking-[0.15em] transition-all relative py-2 ${page === item.page ? 'text-primary' : 'text-black hover:text-primary'}`}>{item.label}{page === item.page && (<motion.div layoutId="nav-underline" className="absolute bottom-0 left-0 right-0 h-1 bg-primary rounded-full" />)}</button>
                    ))}
                </div>
                <div className="flex items-center gap-2 sm:gap-3">
                    <AnimatePresence>
                        {cartCount > 0 && !isCartOpen && !isCheckoutOpen && (
                            <>
                                {/* Desktop/Navbar Cart Button */}
                                <motion.button 
                                    initial={{ scale: 0, opacity: 0 }} 
                                    animate={{ scale: 1, opacity: 1 }} 
                                    exit={{ scale: 0, opacity: 0 }} 
                                    onClick={() => setIsCartOpen(true)} 
                                    className="hidden sm:flex relative w-10 h-10 flex items-center justify-center rounded-xl bg-primary text-white shadow-lg shadow-primary/30 active:scale-90 transition-all group"
                                >
                                    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                                        <path d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z" />
                                    </svg>
                                    <span className="absolute -top-1.5 -right-1.5 bg-red-500 text-white text-[9px] font-black min-w-[18px] h-[18px] px-1 rounded-full flex items-center justify-center border-2 border-white shadow-sm">
                                        {cartCount}
                                    </span>
                                </motion.button>

                                {/* Mobile Floating Cart Button - Draggable */}
                                <motion.button 
                                    initial={{ scale: 0, opacity: 0, y: 20 }} 
                                    animate={{ scale: 1, opacity: 1, y: 0 }} 
                                    exit={{ scale: 0, opacity: 0, y: 20 }} 
                                    onClick={() => setIsCartOpen(true)} 
                                    className="sm:hidden fixed bottom-24 left-1/2 -translate-x-1/2 z-[110] w-12 h-12 flex items-center justify-center rounded-full bg-primary text-white shadow-2xl shadow-primary/40 active:scale-95 transition-all border-4 border-white touch-none"
                                >
                                    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                                        <path d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z" />
                                    </svg>
                                    <span className="absolute -top-1 -right-1 bg-red-500 text-white text-[9px] font-black w-5 h-5 rounded-full flex items-center justify-center border-2 border-white shadow-lg">
                                        {cartCount}
                                    </span>
                                </motion.button>
                            </>
                        )}
                    </AnimatePresence>
                    <button onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)} className={`${iconBtnClass} lg:hidden text-gray-900`}><svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}><path strokeLinecap="round" strokeLinejoin="round" d={isMobileMenuOpen ? "M6 18L18 6M6 6l12 12" : "M4 8h16M4 16h16"} /></svg></button>
                </div>
            </div>
            <AnimatePresence>{isMobileMenuOpen && (<motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }} className="lg:hidden fixed inset-x-0 top-[50px] sm:top-[65px] bg-white shadow-2xl border-t border-gray-100 z-[90] p-5 space-y-4 rounded-b-[1.5rem]"><div className="grid grid-cols-1 gap-1">{navItems.map((item) => (<button key={item.page} onClick={() => handleNavClick(item.page)} className={`text-xs font-black uppercase tracking-widest text-left py-4 px-5 rounded-xl transition-colors ${page === item.page ? 'bg-primary/10 text-primary' : 'text-black active:bg-primary/5'}`}>{item.label}</button>))}</div></motion.div>)}</AnimatePresence>
        </nav>
    );
};

import PrismaNavbar from './prisma_theme/PrismaNavbar';

const NavbarWrapper: React.FC = () => {
    return <PrismaNavbar />;
};

export default NavbarWrapper;
